import { useCallback, useEffect, useRef, useState } from 'react';

import {
  createRecurringAppointments,
  previewRecurringAppointments,
  previewRecurringDates,
} from './recurringAppointmentApi';
import {
  buildRecurringAppointmentCreatePayload,
  buildRecurringAppointmentPreviewPayload,
} from './recurringAppointmentPayload';

const PREVIEW_ERROR = 'Unable to preview recurring appointments. Review the details and try again.';
const DATE_PREVIEW_ERROR = 'Unable to preview recurrence dates. Review the schedule and try again.';
const PREVIEW_REQUIRED_ERROR = 'Generate a current preview before creating recurring appointments.';
const CREATION_ERROR = 'The creation result could not be confirmed. Refresh the calendar before trying again.';
const CREATION_PENDING_ERROR = 'Recurring appointment creation is still pending. Wait and refresh the calendar before continuing.';
const RECONCILIATION_ERROR = 'Refresh the calendar and confirm the prior creation outcome before continuing.';

let creationGate = null;

const cloneAppointment = (appointment = {}) => ({ ...appointment });
const cloneRecurrence = (recurrence = {}) => ({ ...recurrence });
const cloneOverrides = (overrides = []) => overrides.map((override) => ({ ...override }));

const cloneInitialState = ({ appointment = {}, recurrence = {}, overrides = [] } = {}) => ({
  appointment: cloneAppointment(appointment),
  recurrence: cloneRecurrence(recurrence),
  overrides: cloneOverrides(overrides),
});

const hasAuthoritativePreview = (result) => (
  result
  && typeof result.recurrenceBatchId === 'string'
  && result.recurrenceBatchId.length > 0
  && typeof result.previewToken === 'string'
  && result.previewToken.length > 0
  && Array.isArray(result.occurrences)
  && Array.isArray(result.warnings)
  && Number.isSafeInteger(result.expiresAtMs)
);

const useRecurringAppointments = (initialState = {}) => {
  const initialStateRef = useRef(cloneInitialState(initialState));
  const [appointment, setAppointment] = useState(initialStateRef.current.appointment);
  const [recurrence, setRecurrence] = useState(initialStateRef.current.recurrence);
  const [overrides, setOverrides] = useState(initialStateRef.current.overrides);
  const [datePreviewDates, setDatePreviewDates] = useState([]);
  const [datePreviewLoading, setDatePreviewLoading] = useState(false);
  const [datePreviewError, setDatePreviewError] = useState(null);
  const [previewResult, setPreviewResult] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [creationLoading, setCreationLoading] = useState(
    creationGate && creationGate.status === 'pending'
  );
  const [creationError, setCreationError] = useState(null);
  const [creationResult, setCreationResult] = useState(null);
  const [creationOutcomeUncertain, setCreationOutcomeUncertain] = useState(
    creationGate && creationGate.status === 'reconciliation-required'
  );

  const mountedRef = useRef(false);
  const revisionRef = useRef(0);
  const recurrenceRevisionRef = useRef(0);
  const datePreviewRequestRef = useRef(0);
  const previewRequestRef = useRef(0);
  const creationRequestRef = useRef(0);
  const previewInFlightRef = useRef(false);
  const datePreviewInFlightRef = useRef(false);
  const creationInFlightRef = useRef(Boolean(creationGate));
  const authoritativePreviewRef = useRef(null);
  const isCreationLocked = useCallback(() => Boolean(creationGate) || creationInFlightRef.current, []);

  const clearPreview = useCallback(() => {
    authoritativePreviewRef.current = null;
    previewRequestRef.current += 1;
    previewInFlightRef.current = false;
    setPreviewResult(null);
    setPreviewLoading(false);
    setPreviewError(null);
  }, []);

  const clearDatePreview = useCallback(() => {
    datePreviewRequestRef.current += 1;
    datePreviewInFlightRef.current = false;
    setDatePreviewDates([]);
    setDatePreviewLoading(false);
    setDatePreviewError(null);
  }, []);

  const invalidateForEdit = useCallback(() => {
    revisionRef.current += 1;
    clearPreview();
    setCreationError(null);
    setCreationResult(null);
    setCreationOutcomeUncertain(false);
  }, [clearPreview]);

  const setAppointmentDetails = useCallback((nextAppointment) => {
    if (creationInFlightRef.current) return false;
    invalidateForEdit();
    setAppointment((current) => cloneAppointment(
      typeof nextAppointment === 'function'
        ? nextAppointment(cloneAppointment(current))
        : nextAppointment
    ));
    return true;
  }, [invalidateForEdit]);

  const setRecurrenceSettings = useCallback((nextRecurrence) => {
    if (creationInFlightRef.current) return false;
    recurrenceRevisionRef.current += 1;
    clearDatePreview();
    invalidateForEdit();
    setRecurrence((current) => cloneRecurrence(
      typeof nextRecurrence === 'function'
        ? nextRecurrence(cloneRecurrence(current))
        : nextRecurrence
    ));
    return true;
  }, [clearDatePreview, invalidateForEdit]);

  const setOccurrenceOverrides = useCallback((nextOverrides) => {
    if (creationInFlightRef.current) return false;
    invalidateForEdit();
    setOverrides((current) => cloneOverrides(
      typeof nextOverrides === 'function'
        ? nextOverrides(cloneOverrides(current))
        : nextOverrides
    ));
    return true;
  }, [invalidateForEdit]);

  const requestDatePreview = useCallback(async () => {
    if (creationGate) {
      setDatePreviewError(
        creationGate.status === 'pending'
          ? CREATION_PENDING_ERROR
          : RECONCILIATION_ERROR
      );
      return null;
    }
    if (datePreviewInFlightRef.current || creationInFlightRef.current) return null;

    const requestRevision = recurrenceRevisionRef.current;
    const requestId = datePreviewRequestRef.current + 1;
    datePreviewRequestRef.current = requestId;
    datePreviewInFlightRef.current = true;
    setDatePreviewDates([]);
    setDatePreviewError(null);
    setDatePreviewLoading(true);

    try {
      const result = await previewRecurringDates({
        frequency: recurrence.frequency,
        anchor_date: recurrence.anchor_date,
        count: recurrence.count,
        end_date: recurrence.end_date,
      });
      const requestIsCurrent = (
        mountedRef.current
        && datePreviewRequestRef.current === requestId
        && recurrenceRevisionRef.current === requestRevision
      );
      if (!requestIsCurrent) return null;
      if (!Array.isArray(result?.dates) || result.dates.length < 1 || result.dates.length > 100
          || result.dates.some((date) => typeof date !== 'string')) {
        throw new Error('Invalid date preview response.');
      }
      setDatePreviewDates(result.dates);
      return result.dates;
    } catch (error) {
      const requestIsCurrent = (
        mountedRef.current
        && datePreviewRequestRef.current === requestId
        && recurrenceRevisionRef.current === requestRevision
      );
      if (requestIsCurrent) {
        setDatePreviewDates([]);
        setDatePreviewError(error?.message || DATE_PREVIEW_ERROR);
      }
      return null;
    } finally {
      if (
        mountedRef.current
        && datePreviewRequestRef.current === requestId
        && recurrenceRevisionRef.current === requestRevision
      ) {
        datePreviewInFlightRef.current = false;
        setDatePreviewLoading(false);
      }
    }
  }, [recurrence]);

  const requestPreview = useCallback(async () => {
    if (creationGate) {
      setPreviewError(
        creationGate.status === 'pending'
          ? CREATION_PENDING_ERROR
          : RECONCILIATION_ERROR
      );
      return null;
    }
    if (previewInFlightRef.current || creationInFlightRef.current) return null;

    const requestRevision = revisionRef.current;
    const requestId = previewRequestRef.current + 1;
    previewRequestRef.current = requestId;
    previewInFlightRef.current = true;
    authoritativePreviewRef.current = null;
    setPreviewResult(null);
    setPreviewError(null);
    setPreviewLoading(true);
    setCreationError(null);
    setCreationResult(null);
    setCreationOutcomeUncertain(false);

    try {
      const previewPayload = buildRecurringAppointmentPreviewPayload({
        appointment,
        recurrence,
        overrides,
      });
      const result = await previewRecurringAppointments(previewPayload);
      const requestIsCurrent = (
        mountedRef.current
        && previewRequestRef.current === requestId
        && revisionRef.current === requestRevision
      );
      if (!requestIsCurrent) return null;
      if (!hasAuthoritativePreview(result)) throw new Error('Invalid preview response.');

      authoritativePreviewRef.current = {
        revision: requestRevision,
        previewPayload,
        recurrenceBatchId: result.recurrenceBatchId,
        previewToken: result.previewToken,
      };
      setPreviewResult(result);
      return result;
    } catch (_error) {
      const requestIsCurrent = (
        mountedRef.current
        && previewRequestRef.current === requestId
        && revisionRef.current === requestRevision
      );
      if (requestIsCurrent) {
        authoritativePreviewRef.current = null;
        setPreviewResult(null);
        setPreviewError(PREVIEW_ERROR);
      }
      return null;
    } finally {
      if (
        mountedRef.current
        && previewRequestRef.current === requestId
        && revisionRef.current === requestRevision
      ) {
        previewInFlightRef.current = false;
        setPreviewLoading(false);
      }
    }
  }, [appointment, recurrence, overrides]);

  const createRecurringSeries = useCallback(async () => {
    if (creationGate) {
      setCreationError(
        creationGate.status === 'pending'
          ? CREATION_PENDING_ERROR
          : RECONCILIATION_ERROR
      );
      return null;
    }
    if (creationInFlightRef.current) return null;

    const currentPreview = authoritativePreviewRef.current;
    if (!currentPreview || currentPreview.revision !== revisionRef.current) {
      setCreationError(PREVIEW_REQUIRED_ERROR);
      return null;
    }

    const requestId = creationRequestRef.current + 1;
    const requestGate = { status: 'pending' };
    creationRequestRef.current = requestId;
    creationGate = requestGate;
    creationInFlightRef.current = true;
    setCreationLoading(true);
    setCreationError(null);
    setCreationResult(null);
    setCreationOutcomeUncertain(false);

    try {
      const payload = buildRecurringAppointmentCreatePayload({
        previewPayload: currentPreview.previewPayload,
        recurrenceBatchId: currentPreview.recurrenceBatchId,
        previewToken: currentPreview.previewToken,
      });
      const result = await createRecurringAppointments(payload);
      const requestIsCurrent = (
        mountedRef.current
        && creationRequestRef.current === requestId
      );
      if (!requestIsCurrent) {
        if (creationGate === requestGate) {
          requestGate.status = 'reconciliation-required';
        }
        if (mountedRef.current) {
          creationInFlightRef.current = true;
          setCreationLoading(false);
          setCreationError(RECONCILIATION_ERROR);
          setCreationOutcomeUncertain(true);
        }
        return null;
      }

      if (creationGate === requestGate) creationGate = null;
      creationInFlightRef.current = false;
      clearPreview();
      setCreationResult(result);
      return result;
    } catch (_error) {
      if (creationGate === requestGate) {
        requestGate.status = 'reconciliation-required';
      }
      if (mountedRef.current && creationRequestRef.current === requestId) {
        clearPreview();
        setCreationError(CREATION_ERROR);
        setCreationOutcomeUncertain(true);
      } else if (mountedRef.current) {
        creationInFlightRef.current = true;
        setCreationLoading(false);
        setCreationError(RECONCILIATION_ERROR);
        setCreationOutcomeUncertain(true);
      }
      return null;
    } finally {
      if (mountedRef.current && creationRequestRef.current === requestId) {
        creationInFlightRef.current = Boolean(creationGate);
        setCreationLoading(false);
      }
    }
  }, [clearPreview]);

  const reconcileCreationOutcome = useCallback(() => {
    if (creationGate && creationGate.status === 'pending') {
      setCreationError(CREATION_PENDING_ERROR);
      return false;
    }

    creationGate = null;
    creationInFlightRef.current = false;
    setCreationLoading(false);
    setCreationError(null);
    setCreationOutcomeUncertain(false);
    return true;
  }, []);

  const reset = useCallback((nextState) => {
    const resetState = cloneInitialState(nextState || initialStateRef.current);
    revisionRef.current += 1;
    recurrenceRevisionRef.current += 1;
    datePreviewRequestRef.current += 1;
    previewRequestRef.current += 1;
    creationRequestRef.current += 1;
    previewInFlightRef.current = false;
    datePreviewInFlightRef.current = false;
    creationInFlightRef.current = Boolean(creationGate);
    authoritativePreviewRef.current = null;
    setAppointment(resetState.appointment);
    setRecurrence(resetState.recurrence);
    setOverrides(resetState.overrides);
    setDatePreviewDates([]);
    setDatePreviewLoading(false);
    setDatePreviewError(null);
    setPreviewResult(null);
    setPreviewLoading(false);
    setPreviewError(null);
    setCreationLoading(Boolean(creationGate && creationGate.status === 'pending'));
    setCreationError(
      creationGate
        ? creationGate.status === 'pending'
          ? CREATION_PENDING_ERROR
          : RECONCILIATION_ERROR
        : null
    );
    setCreationResult(null);
    setCreationOutcomeUncertain(Boolean(
      creationGate && creationGate.status === 'reconciliation-required'
    ));
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      previewRequestRef.current += 1;
      datePreviewRequestRef.current += 1;
      creationRequestRef.current += 1;
      previewInFlightRef.current = false;
      datePreviewInFlightRef.current = false;
      authoritativePreviewRef.current = null;
    };
  }, []);

  return {
    appointment,
    recurrence,
    overrides,
    setAppointmentDetails,
    setRecurrenceSettings,
    setOccurrenceOverrides,
    datePreviewDates,
    datePreviewLoading,
    datePreviewError,
    requestDatePreview,
    previewResult,
    previewLoading,
    previewError,
    requestPreview,
    creationResult,
    creationLoading,
    creationError,
    creationOutcomeUncertain,
    createRecurringSeries,
    reconcileCreationOutcome,
    canCreate: Boolean(authoritativePreviewRef.current) && !creationLoading && !creationGate,
    isCreationLocked,
    reset,
  };
};

export default useRecurringAppointments;
